import CheckIcon from "@mui/icons-material/Check";
import { Avatar, Box, Chip, Stack, Switch, Typography } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { withForm } from "@/components/Forms";
import { deliveryOptionFormOpts } from "../option.form.opts";

export type OrgRecord = { slug: string; name: string; logo?: string | null };

const COLS = "minmax(0,1fr) 92px 200px 148px";

export function PartnerRowHeader({ dynamicPricing = false }: { dynamicPricing?: boolean }) {
  const { t } = useTranslation();
  const priceLabel = dynamicPricing
    ? t("deliveryOption.visibility.baseOverride")
    : t("deliveryOption.visibility.clientPrice");
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: COLS,
        gap: 1.25,
        pl: "48px",
        pr: 1.25,
        pb: 0.75,
      }}
    >
      {[
        t("deliveryOption.visibility.partner"),
        t("deliveryOption.visibility.active"),
        t("deliveryOption.visibility.displayName"),
        priceLabel,
      ].map((label, i) => (
        <Typography
          key={`${label}-${i}`}
          variant="caption"
          sx={{
            fontSize: "0.68rem",
            fontWeight: 600,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: "text.disabled",
            textAlign: i === 1 ? "center" : "left",
          }}
        >
          {label}
        </Typography>
      ))}
    </Box>
  );
}

export function PartnerRowList({
  form,
  orgs,
  search,
}: {
  form: any;
  orgs: OrgRecord[];
  search: string;
}) {
  const { t } = useTranslation();

  const indexBySlug = useMemo(() => new Map(orgs.map((o, i) => [o.slug, i])), [orgs]);

  const filtered = search
    ? orgs.filter(
        (o) =>
          o.name.toLowerCase().includes(search.toLowerCase()) ||
          o.slug.toLowerCase().includes(search.toLowerCase())
      )
    : orgs;

  return (
    <Stack spacing={0}>
      {filtered.length === 0 ? (
        <Stack alignItems="center" sx={{ py: 4 }}>
          <Typography variant="body2" color="text.disabled">
            {t("deliveryOption.no-results", { query: search })}
          </Typography>
        </Stack>
      ) : (
        <Box>
          <form.Subscribe selector={(s: any) => s.values.dynamicPricing}>
            {(dynamicPricing: boolean) => <PartnerRowHeader dynamicPricing={dynamicPricing} />}
          </form.Subscribe>
          <Stack spacing={0.5}>
            {filtered.map((org, listIndex) => (
              <Box
                key={org.slug}
                sx={{
                  animation: "rowIn 0.18s ease both",
                  animationDelay: `${listIndex * 30}ms`,
                  "@keyframes rowIn": {
                    from: { opacity: 0, transform: "translateY(4px)" },
                    to: { opacity: 1, transform: "translateY(0)" },
                  },
                }}
              >
                <PartnerRow form={form} org={org} index={indexBySlug.get(org.slug) || 0} />
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );
}

const PartnerRow = withForm({
  ...deliveryOptionFormOpts,
  props: { org: {} as OrgRecord, index: 0 },
  render: ({ form, org, index }) => {
    return (
      <form.Subscribe
        selector={(s) => ({
          selected: s.values.partnerAssignments[index]?.selected,
          enabled: s.values.partnerAssignments[index]?.enabled,
        })}
      >
        {({ selected }) => (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              borderRadius: "10px",
              p: "2px",
              cursor: "pointer",
              transition: "background 0.1s",
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            {/* Checkbox */}
            <form.AppField name={`partnerAssignments[${index}].selected`}>
              {(f) => (
                <Box
                  onClick={() => f.handleChange(!f.state.value)}
                  sx={{
                    width: 32,
                    height: 32,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: "6px",
                      border: "1.5px solid",
                      borderColor: selected ? "primary.main" : "divider",
                      bgcolor: selected ? "primary.main" : "transparent",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.15s",
                      color: "primary.contrastText",
                    }}
                  >
                    {selected && <CheckIcon sx={{ fontSize: 12 }} />}
                  </Box>
                </Box>
              )}
            </form.AppField>

            {/* Row card */}
            <Box
              onClick={(e) => {
                if ((e.target as HTMLElement).closest("input,button,[role=button]")) return;
                form.setFieldValue(`partnerAssignments[${index}].selected`, !selected);
              }}
              sx={{
                flex: 1,
                display: "grid",
                gridTemplateColumns: COLS,
                gap: 1.25,
                alignItems: "flex-start",
                px: 1.25,
                py: 1,
                borderRadius: "8px",
                border: "0.5px solid",
                borderColor: selected ? "primary.200" : "divider",
                bgcolor: selected ? "primary.50" : "background.paper",
                opacity: selected ? 1 : 0.45,
                transition: "all 0.15s",
              }}
            >
              {/* Partner identity */}
              <Stack
                direction="row"
                spacing={1.25}
                alignItems="center"
                sx={{ minWidth: 0, height: 40 }}
              >
                <Avatar
                  src={org.logo ?? undefined}
                  variant="rounded"
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "7px",
                    fontSize: 11,
                    fontWeight: 600,
                    bgcolor: selected ? "primary.100" : "action.disabledBackground",
                    color: selected ? "primary.800" : "text.disabled",
                    transition: "all 0.15s",
                    flexShrink: 0,
                  }}
                >
                  {org.name.charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={500} fontSize="0.8125rem" noWrap>
                    {org.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    noWrap
                    sx={{ fontFamily: "monospace", fontSize: "0.68rem", display: "block" }}
                  >
                    {org.slug}
                  </Typography>
                </Box>
              </Stack>

              {/* Active Switch */}
              <Box
                sx={{ display: "flex", justifyContent: "center", height: 40, alignItems: "center" }}
              >
                <form.AppField name={`partnerAssignments[${index}].enabled`}>
                  {(f) => {
                    const isOn = f.state.value && selected;
                    return (
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="center"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (selected) f.handleChange(!f.state.value);
                        }}
                        sx={{
                          cursor: selected ? "pointer" : "not-allowed",
                          opacity: selected ? 1 : 0.5,
                        }}
                      >
                        <Switch
                          size="medium"
                          checked={isOn}
                          disabled={!selected}
                          sx={{ "& .MuiSwitch-thumb": { boxShadow: "0 2px 4px rgba(0,0,0,0.2)" } }}
                        />
                      </Stack>
                    );
                  }}
                </form.AppField>
              </Box>

              {/* Display name */}
              <form.AppField name={`partnerAssignments[${index}].displayName`}>
                {(f) => (
                  <form.Subscribe selector={(s) => s.values.name}>
                    {(optionName) => (
                      <f.TextField legend={false} placeholder={optionName} disabled={!selected} />
                    )}
                  </form.Subscribe>
                )}
              </form.AppField>

              {/* Client price / base override */}
              <form.AppField name={`partnerAssignments[${index}].clientPrice`}>
                {(f) => (
                  <form.Subscribe selector={(s) => s.values.defaultClientPrice}>
                    {(defaultPrice) => (
                      <f.PriceField
                        min={0}
                        suffix="€"
                        fallback={defaultPrice}
                        disabled={!selected}
                      />
                    )}
                  </form.Subscribe>
                )}
              </form.AppField>
            </Box>
          </Stack>
        )}
      </form.Subscribe>
    );
  },
});
